import React, { useEffect, useState } from 'react';
import Layout from './Layout'; // Assuming Layout component exists
import { defaultAxiosInstance } from '../api/defaultInstance'; // Assuming axios instance setup
import '../css/AdManagement.css'; // Reusing existing CSS, adjust classNames or CSS file if needed
import { FaCheckDouble, FaSpinner, FaSearch } from 'react-icons/fa'; // Icons for status, loading, and search/filter apply

function SettlementManagement() {
    const [isLoading, setIsLoading] = useState(false); // Loading state for the main action button
    const [isFetching, setIsFetching] = useState(false); // Loading state for fetching data
    const [settlements, setSettlements] = useState([]);
    
    const [filter, setFilter] = useState({
        yearMonth: '', // Initialize with current month in YYYY-MM
        status: '',
        email: '', // Changed from userId based on DTO
        name: '',
        referralCode: '',
        page: 1,
        limit: 50 // Default limit, DTO default is 10, adjust if needed
    });

    const [selectedSettlements, setSelectedSettlements] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Fetch settlement data from the API
    const fetchSettlementData = async (currentFilter = filter) => {
      setIsFetching(true);
      try {
          const apiParams = {
              // Directly use the yearMonth from the filter state
              yearMonth: currentFilter.yearMonth || undefined,
  
              // Map status ('notComplete', 'complete', '') to backend values ('1', '2', undefined)
              status: currentFilter.status,
  
              // Use email, name, referralCode from filter state
              email: currentFilter.email || undefined, // Use email based on DTO
              name: currentFilter.name || undefined,
              referralCode: currentFilter.referralCode || undefined,
  
              // Include page and limit
              page: currentFilter.page,
              limit: currentFilter.limit
          };
  
          // Remove undefined keys before sending (optional, depends on backend tolerance)
          Object.keys(apiParams).forEach(key => apiParams[key] === undefined && delete apiParams[key]);
  
          const response = await defaultAxiosInstance.get('/settlement/admin/requests', {
              params: apiParams // Send the constructed params
          });
          setTotalCount(response.data.data.total)
          setSettlements(response.data.data.list)
  
          // ... rest of the function ...
      } catch (error) {
          // ... error handling ...
      } finally {
          setIsFetching(false);
      }
    };

    // Initial data load
    useEffect(() => {
        fetchSettlementData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Fetch on mount

    // Handle changes in filter inputs
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
  
        setFilter(prev => ({
             ...prev,
             [name]: value,
             // Reset page to 1 when any filter except page changes
             page: name !== 'page' ? 1 : prev.page
        }));
    };

    // Apply filters and fetch data
    const applyFilter = () => {
        // Ensure page is 1 when applying filters manually
        const newFilter = { ...filter, page: 1 };
        setFilter(newFilter); // Update state to reflect page reset
        fetchSettlementData(newFilter); // Fetch with the reset filter
    };

    // Handle individual row checkbox change
    const handleCheckboxChange = (settlementId) => {
        setSelectedSettlements((prev) =>
            prev.includes(settlementId) ? prev.filter((id) => id !== settlementId) : [...prev, settlementId]
        );
    };

    // Handle "select all" checkbox change
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select only IDs from the currently visible page/data
            setSelectedSettlements(settlements.map(s => s.settlementRequestId));
        } else {
            setSelectedSettlements([]);
        }
    };

    // --- Pagination Handlers ---
    const handlePageClick = (page) => {
        const newFilter = { ...filter, page };
        setFilter(newFilter);
        fetchSettlementData(newFilter);
    };

    const handlePrevPage = () => {
        if (filter.page > 1) {
            handlePageClick(filter.page - 1);
        }
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalCount / filter.limit);
        if (filter.page < totalPages) {
            handlePageClick(filter.page + 1);
        }
    };
    // --- End Pagination Handlers ---


    // Handle "Withdrawal Complete" button click
    const handleConfirmWithdrawal = async () => {
        if (selectedSettlements.length === 0) {
            alert("출금 완료 처리할 항목을 선택해주세요.");
            return;
        }
        setIsLoading(true); // Start loading indicator for the button
        try {
          // 1. Find the full settlement objects corresponding to the selected IDs
          const selectedSettlementObjects = settlements.filter(item =>
            selectedSettlements.includes(item.settlementRequestId)
          );

          // 2. Map these full objects to the format required by ConfirmSettlementItem
          const settlementInfoList = selectedSettlementObjects.map(item => {
              // *** 중요: 'settlements' 배열의 각 item 객체에 아래 필드들이 정확히 있는지 확인하세요. ***
              // (API `/settlement/admin/requests` 응답 데이터 구조 확인 필요)
              return {
                  userId: item.userId, // item 객체에 userId 필드가 있어야 함
                  settlementRequestId: item.settlementRequestId,
                  amount: item.amount,
                  fee: item.fee,
                  settlementValue: item.settlementValue
              };
          });

          // *** Replace with your actual API endpoint for confirming settlements ***
          const response = await defaultAxiosInstance.post('/settlement/admin/confirm', {
              // Adjust payload key if backend expects something else (e.g., requestIds)
              settlementInfoList: settlementInfoList
          });

          if (response.data.status === 200) {
              alert("선택된 항목이 출금 완료 처리되었습니다.");
              fetchSettlementData(); // Refresh data to show updated status
              setSelectedSettlements([]); // Clear selection
          } else {
              // Handle potential API-specific error messages
              alert(response.data.message || "출금 완료 처리 중 오류가 발생했습니다.");
          }
        } catch (error) {
            console.error("Error confirming withdrawal:", error);
            alert("출금 완료 처리 중 오류가 발생했습니다.");
        } finally {
            setIsLoading(false); // Stop loading indicator
        }
    };

    // Map status code to readable text and CSS class
    const getStatusInfo = (status) => {
        switch (status) {
            case 0: return { text: '미완료', className: 'notComplete' }; // Or 'waiting' if that class exists
            case 1: return { text: '완료', className: 'complete' }; // Or 'verified' if that class exists
        }
    };


    // Calculate pagination display info
    const startIndex = totalCount === 0 ? 0 : (filter.page - 1) * filter.limit + 1;
    const endIndex = Math.min(filter.page * filter.limit, totalCount);
    const totalPages = Math.ceil(totalCount / filter.limit);

    return (
        <Layout>
            <div className="ad-management-container"> {/* Reusing container class */}
                {/* Filter Bar */}
                <div className="filter-bar3"> {/* Reusing filter bar class */}
                    <div className="filter-group">
                      <label>정산월</label>
                      <input
                          type="month"
                          name="yearMonth" // Matches the state key and DTO field
                          value={filter.yearMonth}
                          onChange={handleFilterChange}
                          // Optional: Set max to prevent selecting future months if needed
                          // max={initialYearMonth}
                      />
                  </div>
                    <div className="filter-group">
                        <label>출금 완료 여부</label>
                        <select name="status" value={filter.status} onChange={handleFilterChange}>
                            <option value="">전체</option>
                            <option value="notComplete">미완료</option>
                            <option value="complete">완료</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>아이디</label>
                        <input
                            type="text"
                            name="email"
                            placeholder="회원 ID 검색"
                            value={filter.email}
                            onChange={handleFilterChange}
                         />
                    </div>
                    <div className="filter-group">
                        <label>이름</label>
                        <input
                            type="text"
                            name="name"
                            placeholder="회원 이름 검색"
                            value={filter.name}
                            onChange={handleFilterChange}
                        />
                    </div>
                     <div className="filter-group">
                        <label>초대코드</label>
                        <input
                            type="text"
                            name="referralCode"
                            placeholder="초대 코드 검색"
                            value={filter.referralCode}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <div className="filter-group">
                        <button onClick={applyFilter} disabled={isFetching}>
                            <FaSearch /> {isFetching ? '조회중...' : '조회'}
                        </button>
                    </div>
                </div>

                {/* Action Buttons Header */}
                <div className="table-header">
                    <h3 className="section-title">정산 요청 목록</h3>
                     {/* Only show button if not currently fetching data */}
                     {!isFetching && (
                        <div className="button-group" style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.7 : 1 }}>
                             <button
                                className="status-btn complete" // Use a suitable class like 'complete' or 'paid'
                                onClick={handleConfirmWithdrawal}
                                disabled={isLoading || selectedSettlements.length === 0} // Disable if loading or nothing selected
                            >
                                {isLoading ? <FaSpinner className="spinner" /> : <FaCheckDouble />} {/* Use FaCheckDouble or similar */}
                                {isLoading ? '처리 중...' : '출금완료 처리'}
                            </button>
                        </div>
                     )}
                </div>

                {/* Data Table */}
                <div className="ad-table"> {/* Reusing table wrapper class */}
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        // Check if all *currently loaded* items are selected
                                        checked={settlements.length > 0 && selectedSettlements.length === settlements.length}
                                        disabled={settlements.length === 0} // Disable if no data
                                    />
                                </th>
                                <th>회원 ID</th>
                                <th>이름</th>
                                <th>초대 코드</th>
                                <th>지급 금액(파이)</th>
                                <th>수수료(파이)</th>
                                <th>전환 금액(원)</th>
                                <th>지급 여부</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isFetching ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center' }}><FaSpinner className="spinner" /> 데이터를 불러오는 중...</td></tr>
                            ) : settlements.length === 0 ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center' }}>조회된 데이터가 없습니다.</td></tr>
                            ) : (
                                settlements.map((item) => {
                                    const statusInfo = getStatusInfo(item.status);
                                    return (
                                        <tr key={item.settlementRequestId}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSettlements.includes(item.settlementRequestId)}
                                                    onChange={() => handleCheckboxChange(item.settlementRequestId)}
                                                />
                                            </td>
                                            {/* Displaying data based on the API response structure */}
                                            <td>{item.email}</td>
                                            <td>{item.name}</td>
                                            <td>{item.referralCode}</td>
                                            {/* Apply formatting if needed */}
                                            <td>{item.amount?.toLocaleString()} 파이</td>
                                            <td>{item.fee?.toLocaleString()} 파이</td>
                                            <td>{item.settlementValue?.toLocaleString()} 원</td>
                                            <td>
                                                {/* Using badge style similar to the original component */}
                                                <span className={`badge badge-${statusInfo.className}`}>
                                                    {statusInfo.text}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Info and Controls */}
                <div className="pagination-info">
                    <div>총 {totalCount.toLocaleString()}개 항목 중 {startIndex.toLocaleString()} - {endIndex.toLocaleString()}</div>
                    <div className="pagination">
                        <button onClick={handlePrevPage} disabled={filter.page === 1 || isFetching}>이전</button>
                        {/* Simple pagination: show limited page numbers if needed */}
                        {/* Example: Always show first, last, current, and adjacent pages */}
                        {/* For simplicity, showing all page numbers if not too many */}
                        {totalPages <= 10 ? ( // Show all if 10 or less pages
                            Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    className={filter.page === page ? "active" : ""}
                                    onClick={() => handlePageClick(page)}
                                    disabled={isFetching}
                                >
                                    {page}
                                </button>
                            ))
                        ) : ( // Basic implementation for many pages (can be improved)
                            <>
                                {filter.page > 1 && <button onClick={() => handlePageClick(1)} disabled={isFetching}>1</button>}
                                {filter.page > 2 && <span>...</span>}
                                {filter.page > 0 && <button className="active" disabled>{filter.page}</button>}
                                {filter.page < totalPages -1 && <span>...</span>}
                                {filter.page < totalPages && <button onClick={() => handlePageClick(totalPages)} disabled={isFetching}>{totalPages}</button>}
                             </>
                        )}
                        <button onClick={handleNextPage} disabled={filter.page >= totalPages || isFetching}>다음</button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default SettlementManagement;